import {
	Component, Input, Output, EventEmitter, OnInit, OnDestroy,
	OnChanges, SimpleChange, SimpleChanges
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { LicenseInfoDto, LicenseWizardRequestDto, LicenseWizardResultDto } from 'UvExplorerServer.Dtos.TS/LicenseStats';
import { LicensingService } from '../../services/licensing.service';
import { MessageService } from 'base';

@Component({
    selector: 'activation-wizard',
	templateUrl: './activation-wizard.component.html',
	styleUrls: ['./activation-wizard.component.scss']

})
export class ActivationWizardComponent implements OnInit, OnDestroy, OnChanges {

	@Input()
	public visible: boolean;

	@Output()
	public visibleChange: EventEmitter<boolean>;

	@Output()
	public onOk: EventEmitter<any>;

	@Output()
	public onCancel: EventEmitter<any>;

	public backLabel: string;
	public nextLabel: string;
	public cancelLabel: string;

	public readonly WelcomePage = "welcome";
	public readonly ProductKeyPage = "product-key";
	public readonly OnlineFailedPage = "online-failed";
	public readonly OfflineQueryPage = "offline-query";
	public readonly OfflinePage = "offline";
	public readonly OfflineFailedPage = "offline-failed";
	public readonly MultipleTrialPage = "multiple-trial";
	public readonly CompletionPage = "completion";

	public page: string;
	private activationRequest: string;
	private activationResponse: string;

	public productKeyControl: UntypedFormControl;
	public productKeyForm: UntypedFormGroup;

	public onlineFailedLabel: string;
	public offlineFailedLabel: string;

	private ok: boolean;

	constructor(private licService: LicensingService,
		private messageService: MessageService,
		private fb: UntypedFormBuilder) {

		this.visible = false;
		this.visibleChange = new EventEmitter<boolean>();
		this.onOk = new EventEmitter<any>();
		this.onCancel = new EventEmitter<any>();
	}

	public get isActivated() {
		return this.licService.isActivated;
	}

	private resetDialog(): void {
		this.backLabel = "< Back";
		this.nextLabel = "Next >";
		this.cancelLabel = "Cancel";

		this.page = "";
		this.activationRequest = "";
		this.activationResponse = "";

		this.ok = false;

		this.resetOnlineFailedLabel();
		this.resetOfflineFailedLabel();

		this.buildForms();

		this.setPage(this.WelcomePage);

		this.updateIsActivated();
	}

	private resetOnlineFailedLabel(): void {
		this.onlineFailedLabel = "License activation failed.";
	}

	private resetOfflineFailedLabel(): void {
		this.offlineFailedLabel = "License activation failed.";
	}

	public ngOnInit(): void {
		//this.resetDialog();
	}

	public ngOnDestroy(): void {
	}

	public ngOnChanges(changes: SimpleChanges): void {
		let change: SimpleChange = null;

		change = changes["visible"];
		if (change) {
			if (this.visible) {
				this.resetDialog();
			}
		}
	}

	private buildForms(): void {
		this.productKeyControl = new UntypedFormControl("", Validators.required);
		this.productKeyForm = this.fb.group({
			productKeyControl: this.productKeyControl,
		});
	}

	public show(): void {
		this.visible = true;
		this.visibleChange.emit(true);
	}

	public hide(): void {
		this.visible = false;
		this.visibleChange.emit(false);
	}

	private updateIsActivated(): void {
		this.licService.getLicenseInfo().subscribe(
			(result: LicenseInfoDto) => {
				// do nothing
			},
			(error) => {
				this.messageService.displayErrorMessage("Error getting activation status");
			});		
	}

	private setPage(newPage: string): void {

		this.page = newPage;

		if (this.page === this.ProductKeyPage ||
			this.page === this.OfflinePage) {

			this.nextLabel = "Activate >";
		}
		else if (this.page === this.CompletionPage) {
			this.ok = true;
			this.nextLabel = "Finish";
		}
		else {
			this.nextLabel = "Next >";
		}

		if (this.page === this.OfflinePage) {
			console.assert(!!this.productKeyControl.value);
			let request = new LicenseWizardRequestDto("start-offline-page");
			request.encodedProductKey = this.productKeyControl.value;
			this.activationRequest = "";
			this.licService.licenseActivationWizard(request).subscribe(
				(result: LicenseWizardResultDto) => {
					if (result.succeeded) {
						this.activationRequest = result.activationRequest;
					}
					else {
						this.messageService.displayErrorMessage(result.errorMessage);
					}
				},
				(error) => {
					this.messageService.displayErrorMessage("Error starting offline activation");
					this.setPage(this.OfflineQueryPage);
				});
		}
	}

	public onSaveActivationRequest(): void {
		if (!!this.activationRequest) {
			this.download(this.activationRequest, "activation-request.xml", "text/plain");
		}
		else {
			this.messageService.displayErrorMessage("Activation request is empty");
		}
	}

	public onLoadActivationResponse(actResponseFile: any): void {
		if (!!actResponseFile) {
			this.activationResponse = "";
			var reader = new FileReader();
			reader.onload = (e: any) => {
				var content = e.target.result;
				if (content.match(/<Response>/)) {
					this.activationResponse = content;
				}
			};
			reader.readAsText(actResponseFile);
		}
	}

	// Function to download data to a file
	private download(data, filename, type): void {
		var file;
		if (data instanceof Blob) {
			file = data;
		}
		else {
			file = new Blob([data], { type: type });
		}

		var a = document.createElement("a"),
			url = URL.createObjectURL(file);
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		setTimeout(function () {
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		}, 0);
		
	}

	public get enableBack(): boolean {
		if (this.page === this.WelcomePage) {
			return false;
		}
		else if (this.page === this.ProductKeyPage) {
			return true;
		}
		else if (this.page === this.OnlineFailedPage) {
			return true;
		}
		else if (this.page === this.OfflineQueryPage) {
			return true;
		}
		else if (this.page === this.OfflinePage) {
			return true;
		}
		else if (this.page === this.OfflineFailedPage) {
			return true;
		}
		else if (this.page === this.MultipleTrialPage) {
			return true;
		}
		else if (this.page === this.CompletionPage) {
			return false;
		}
		else {
			return false;
		}
	}

	public get enableNext(): boolean {
		if (this.page === this.WelcomePage) {
			return true;
		}
		else if (this.page === this.ProductKeyPage) {
			return (this.productKeyControl.valid && !this.isActivated);
		}
		else if (this.page === this.OnlineFailedPage) {
			return false;
		}
		else if (this.page === this.OfflineQueryPage) {
			return true;
		}
		else if (this.page === this.OfflinePage) {
			return (!!this.activationResponse && !this.isActivated);
		}
		else if (this.page === this.OfflineFailedPage) {
			return false;
		}
		else if (this.page === this.MultipleTrialPage) {
			return false;
		}
		else if (this.page === this.CompletionPage) {
			return true;
		}
		else {
			return false;
		}
	}

	public get enableCancel(): boolean {
		return (this.page !== this.CompletionPage);
	}

	public onDialogHide(): void {
		this.hide();
		if (this.ok) {
			this.onOk.emit();
		}
		else {
			this.onCancel.emit();
		}
	}

	public onCancelClicked(): void {
		this.hide();
	}

	public onNextClicked(): void {

		if (this.page === this.WelcomePage) {
			this.setPage(this.ProductKeyPage);
		}
		else if (this.page === this.ProductKeyPage) {
			console.assert(!!this.productKeyControl.value);
			let request = new LicenseWizardRequestDto("next-product-key-page");
			request.encodedProductKey = this.productKeyControl.value;
			this.resetOnlineFailedLabel();
			this.messageService.startWork();
			this.licService.licenseActivationWizard(request).subscribe(
				(result: LicenseWizardResultDto) => {
					this.messageService.endWork();
					if (result.nextPage === this.OnlineFailedPage) {
						this.onlineFailedLabel = result.errorMessage;
					}
					this.setPage(result.nextPage);
				},
				(error) => {
					this.messageService.endWork();
					this.messageService.displayErrorMessage("Error activating license");
				});
		}
		else if (this.page === this.OfflineQueryPage) {
			this.setPage(this.OfflinePage);
		}
		else if (this.page === this.OfflinePage) {
			console.assert(!!this.productKeyControl.value);
			console.assert(!!this.activationResponse);
			let request = new LicenseWizardRequestDto("next-offline-page");
			request.encodedProductKey = this.productKeyControl.value;
			request.activationResponse = this.activationResponse;
			this.resetOfflineFailedLabel();
			this.messageService.startWork();
			this.licService.licenseActivationWizard(request).subscribe(
				(result: LicenseWizardResultDto) => {
					this.messageService.endWork();
					if (result.nextPage === this.OfflineFailedPage) {
						this.offlineFailedLabel = result.errorMessage;
					}
					this.setPage(result.nextPage);
				},
				(error) => {
					this.messageService.endWork();
					this.messageService.displayErrorMessage("Error activating license");
				});
		}
		else if (this.page === this.CompletionPage) {
			this.hide();
		}
	}

	public onBackClicked(): void {

		if (this.page === this.ProductKeyPage) {
			this.setPage(this.WelcomePage);
		}
		if (this.page === this.OnlineFailedPage) {
			this.setPage(this.ProductKeyPage);
		}
		else if (this.page === this.OfflineQueryPage || this.page === this.MultipleTrialPage) {
			this.setPage(this.ProductKeyPage);
		}
		else if (this.page === this.OfflinePage) {
			this.setPage(this.OfflineQueryPage);
		}
		else if (this.page === this.OfflineFailedPage) {
			this.setPage(this.OfflinePage);
		}
	}

}